export interface EndpointLatencyResult {
  name: string;
  domain: string;
  wsLatencies: number[];
  wsMin: number;
  wsMax: number;
  wsAvg: number;
  wsJitter: number;
  httpLatency: number | null;
  clientIp: string | null;
  geo: string | null;
  connectionTiming: {
    dns: number | null;
    tcp: number | null;
    tls: number | null;
  } | null;
}

export interface LatencyResult {
  endpoints: EndpointLatencyResult[];
}

const BACKENDS = [
  { name: "ALI", domain: "nettool-va-ali.rivergame.net" },
  { name: "GCP", domain: "nettool-va-gcp.topwargame.com" },
  { name: "CF", domain: "nettool-va-cf.topwarapp.com" },
  { name: "AWS", domain: "nettool-va-aws.topwargame.com" },
];

function calculateStats(values: number[]) {
  if (values.length === 0) return { min: 0, max: 0, avg: 0, jitter: 0 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((sum, v) => sum + (v - avg) ** 2, 0) / values.length;
  const jitter = Math.sqrt(variance);
  return { min, max, avg: Math.round(avg), jitter: Math.round(jitter * 10) / 10 };
}

function measureWsLatency(domain: string, pingCount: number = 10): Promise<number[]> {
  return new Promise((resolve) => {
    const latencies: number[] = [];
    let sent = 0;

    try {
      const ws = new WebSocket(`wss://${domain}/ws`);

      ws.onopen = () => {
        sendPing();
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === "pong" && data.clientTs) {
          const rtt = Date.now() - data.clientTs;
          latencies.push(rtt);
          if (latencies.length >= pingCount) {
            ws.close();
            resolve(latencies);
          } else {
            setTimeout(sendPing, 100);
          }
        }
      };

      ws.onerror = () => {
        resolve(latencies);
      };

      ws.onclose = () => {
        resolve(latencies);
      };

      function sendPing() {
        if (sent >= pingCount) return;
        sent++;
        ws.send(JSON.stringify({ type: "ping", clientTs: Date.now() }));
      }

      setTimeout(() => {
        ws.close();
        resolve(latencies);
      }, 15000);
    } catch {
      resolve(latencies);
    }
  });
}

async function measureHttpLatency(domain: string): Promise<{ latency: number | null; clientIp: string | null; geo: string | null }> {
  let latency: number | null = null;
  let clientIp: string | null = null;
  let geo: string | null = null;

  try {
    const start = performance.now();
    const res = await fetch(`https://${domain}/health`, { mode: "cors" });
    await res.json();
    latency = Math.round(performance.now() - start);
  } catch {}

  try {
    const res = await fetch(`https://${domain}/api/client-info`, { mode: "cors", signal: AbortSignal.timeout(5000) });
    if (res.ok) {
      const data = await res.json();
      clientIp = data.clientIp || null;
      if (data.geo) {
        geo = [data.geo.country, data.geo.city].filter(Boolean).join(" ") || null;
      }
    }
  } catch {}

  return { latency, clientIp, geo };
}

function getConnectionTiming(domain: string): { dns: number | null; tcp: number | null; tls: number | null } | null {
  try {
    const entries = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
    const entry = entries.find((e) => e.name.includes(domain) && e.name.includes("/health"));
    if (!entry) return null;
    const dns = entry.domainLookupEnd - entry.domainLookupStart;
    const tcp = entry.connectEnd - entry.connectStart;
    const tls = entry.secureConnectionStart > 0 ? entry.connectEnd - entry.secureConnectionStart : null;
    return {
      dns: dns >= 0 ? Math.round(dns) : null,
      tcp: tcp >= 0 ? Math.round(tcp) : null,
      tls: tls != null ? Math.round(tls) : null,
    };
  } catch {
    return null;
  }
}

async function measureEndpoint(backend: { name: string; domain: string }): Promise<EndpointLatencyResult> {
  const [wsLatencies, httpResult] = await Promise.all([
    measureWsLatency(backend.domain, 10),
    measureHttpLatency(backend.domain),
  ]);

  const stats = calculateStats(wsLatencies);
  const connectionTiming = getConnectionTiming(backend.domain);

  return {
    name: backend.name,
    domain: backend.domain,
    wsLatencies,
    wsMin: stats.min,
    wsMax: stats.max,
    wsAvg: stats.avg,
    wsJitter: stats.jitter,
    httpLatency: httpResult.latency,
    clientIp: httpResult.clientIp,
    geo: httpResult.geo,
    connectionTiming,
  };
}

export async function measureLatency(
  onProgress?: (completed: number, total: number) => void
): Promise<LatencyResult> {
  const results: EndpointLatencyResult[] = [];

  const promises = BACKENDS.map(async (backend) => {
    const result = await measureEndpoint(backend);
    results.push(result);
    onProgress?.(results.length, BACKENDS.length);
    return result;
  });

  await Promise.all(promises);

  const ordered = BACKENDS.map((b) => results.find((r) => r.domain === b.domain)!);
  return { endpoints: ordered };
}
